import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from '../ui/calendar';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { X, CalendarDays } from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, isAfter, isBefore } from 'date-fns';

export const AvailabilityCalendar = ({
  value = [],
  onChange,
  maxDates = 30,
  readOnly = false
}) => {
  const { t } = useTranslation();

  // Convert string dates to Date objects for the calendar
  const selectedDates = value.map(dateStr => new Date(dateStr));

  // Handle date selection
  const handleSelect = (dates) => {
    if (readOnly) return;

    if (!dates) {
      onChange([]);
      return;
    }

    // Convert Date objects back to ISO strings (YYYY-MM-DD format)
    const dateStrings = dates
      .filter(d => d instanceof Date && !isNaN(d))
      .map(d => format(d, 'yyyy-MM-dd'))
      .slice(0, maxDates);

    onChange(dateStrings);
  };

  // Remove a specific date
  const handleRemoveDate = (dateToRemove) => {
    if (readOnly) return;
    const newDates = value.filter(d => d !== dateToRemove);
    onChange(newDates);
  };

  // Clear all dates
  const handleClearAll = () => {
    if (readOnly) return;
    onChange([]);
  };

  // Select this week (today + 6 days)
  const handleSelectThisWeek = () => {
    if (readOnly) return;
    const today = new Date();
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      weekDates.push(format(addDays(today, i), 'yyyy-MM-dd'));
    }

    // Merge with existing dates, remove duplicates, limit to maxDates
    const merged = [...new Set([...value, ...weekDates])].slice(0, maxDates);
    onChange(merged);
  };

  // Select next week
  const handleSelectNextWeek = () => {
    if (readOnly) return;
    const nextWeekStart = addDays(new Date(), 7);
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      weekDates.push(format(addDays(nextWeekStart, i), 'yyyy-MM-dd'));
    }

    const merged = [...new Set([...value, ...weekDates])].slice(0, maxDates);
    onChange(merged);
  };

  // Disable past dates
  const disabledDays = { before: new Date() };

  // Format selected dates for display
  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, 'MMM d, yyyy');
  };

  // Sort dates for display
  const sortedDates = [...value].sort((a, b) => new Date(a) - new Date(b));

  if (readOnly) {
    // Read-only view for profile display
    if (sortedDates.length === 0) {
      return (
        <div className="text-sm text-muted-foreground">
          {t('profile.noAvailabilitySet') || 'No specific dates set'}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <CalendarDays size={16} />
          <span>{t('profile.availableOn') || 'Available on'}:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {sortedDates.map((dateStr) => (
            <Badge key={dateStr} variant="secondary">
              {formatDisplayDate(dateStr)}
            </Badge>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarDays size={18} />
          {t('profile.availabilityCalendar') || 'Availability Calendar'}
        </CardTitle>
        <CardDescription>
          {t('profile.selectAvailableDates') || 'Select the dates you are available to work'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSelectThisWeek}
            disabled={value.length >= maxDates}
          >
            {t('profile.thisWeek') || 'This Week'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSelectNextWeek}
            disabled={value.length >= maxDates}
          >
            {t('profile.nextWeek') || 'Next Week'}
          </Button>
          {value.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="text-destructive hover:text-destructive"
            >
              {t('profile.clearAll') || 'Clear All'}
            </Button>
          )}
        </div>

        {/* Calendar */}
        <div className="flex justify-center">
          <Calendar
            mode="multiple"
            selected={selectedDates}
            onSelect={handleSelect}
            disabled={disabledDays}
            numberOfMonths={1}
            className="rounded-md border"
          />
        </div>

        {/* Selected Dates Count */}
        <div className="text-sm text-muted-foreground text-center">
          {value.length} / {maxDates} {t('profile.datesSelected') || 'dates selected'}
        </div>

        {/* Selected Dates Chips */}
        {sortedDates.length > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('profile.selectedDates') || 'Selected Dates'}:
            </label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {sortedDates.map((dateStr) => (
                <Badge
                  key={dateStr}
                  variant="secondary"
                  className="flex items-center gap-1 pr-1"
                >
                  {formatDisplayDate(dateStr)}
                  <button
                    type="button"
                    onClick={() => handleRemoveDate(dateStr)}
                    className="ml-1 hover:bg-muted rounded-full p-0.5"
                  >
                    <X size={12} />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
